/**
 * Startbestand Ernährung: die Standardgerichte aus dem Prototyp.
 *
 * **Herkunft ehrlich benannt.** Diese Nährwerte sind die Schätzungen aus
 * `kraftwerk-prototyp/data.js` — von dir für deine üblichen Gerichte gemacht,
 * nicht nachgewogen und nicht aus einer Nährwertdatenbank. Genau das trägt jedes
 * Rezept als `sourceNote` mit, und die App zeigt es an. Eine Zahl ohne Herkunft
 * wäre in diesem Projekt ein Fehler; eine Schätzung *mit* Herkunft ist brauchbar.
 *
 * **Bewusst keine Lebensmittel-Tabelle.** Werte je 100 g für Tofu, Hafer oder
 * Sojamilch könnte ich aus dem Gedächtnis hinschreiben — sie sähen aus wie
 * geprüfte Daten und wären keine. Die beste Quelle steht ohnehin auf der
 * Packung, die du in der Hand hast: Der Editor legt Lebensmittel in Sekunden an
 * und trägt „Quelle: Verpackung" vor.
 */

import type { MealSlot } from './nutrition.js';

export interface CatalogRecipe {
  key: string;
  name: string;
  nameEn: string;
  slot: MealSlot;
  /** Nährwerte **je Portion** — der Startbestand ist auf eine Portion gerechnet. */
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  /** Die Zutatenzeile als Text, wie im Prototyp. Zutaten mit Gramm kommen im Editor. */
  items: string;
  itemsEn: string;
}

/** Alle Werte je Portion. Quelle: Prototyp `data.js`, eigene Schätzung. */
export const STARTER_RECIPES: readonly CatalogRecipe[] = [
  {
    key: 'oats',
    slot: 'breakfast',
    name: 'Protein-Overnight-Oats',
    nameEn: 'Protein overnight oats',
    kcal: 620,
    protein: 38,
    carbs: 78,
    fat: 16,
    items: '80 g Haferflocken · 300 ml Sojamilch · 30 g Sojaprotein · 20 g Erdnussmus · Beeren',
    itemsEn: '80 g oats · 300 ml soy milk · 30 g soy protein · 20 g peanut butter · berries',
  },
  {
    key: 'tofuscr',
    slot: 'breakfast',
    name: 'Tofu-Rührei mit Vollkorntoast',
    nameEn: 'Tofu scramble with wholegrain toast',
    kcal: 580,
    protein: 42,
    carbs: 52,
    fat: 22,
    items: '200 g Tofu · Kala Namak · Paprika · 2 Scheiben Vollkorntoast · Hefeflocken',
    itemsEn:
      '200 g tofu · kala namak · bell pepper · 2 slices wholegrain toast · nutritional yeast',
  },
  {
    key: 'shakebf',
    slot: 'breakfast',
    name: 'Schneller Shake + Brot',
    nameEn: 'Quick shake + bread',
    kcal: 540,
    protein: 40,
    carbs: 62,
    fat: 14,
    items: '30 g Protein · 300 ml Sojamilch · Banane · 1 Scheibe Vollkornbrot mit Hummus',
    itemsEn: '30 g protein · 300 ml soy milk · banana · 1 slice wholegrain bread with hummus',
  },
  {
    key: 'lentil',
    slot: 'lunch',
    name: 'Linsen-Bolognese mit Vollkornpasta',
    nameEn: 'Lentil bolognese with wholegrain pasta',
    kcal: 780,
    protein: 45,
    carbs: 108,
    fat: 16,
    items: '120 g Vollkornpasta (trocken) · 150 g rote Linsen gekocht · Tomatensauce · Hefeflocken',
    itemsEn:
      '120 g wholegrain pasta (dry) · 150 g cooked red lentils · tomato sauce · nutritional yeast',
  },
  {
    key: 'bowl',
    slot: 'lunch',
    name: 'Tempeh-Reis-Bowl',
    nameEn: 'Tempeh rice bowl',
    kcal: 750,
    protein: 44,
    carbs: 88,
    fat: 22,
    items: '150 g Tempeh · 90 g Reis (trocken) · Brokkoli · Sesam · Sojasauce-Ingwer-Dressing',
    itemsEn: '150 g tempeh · 90 g rice (dry) · broccoli · sesame · soy-ginger dressing',
  },
  {
    key: 'seitan',
    slot: 'lunch',
    name: 'Seitan-Wrap',
    nameEn: 'Seitan wrap',
    kcal: 700,
    protein: 52,
    carbs: 74,
    fat: 18,
    items: '150 g Seitan · 2 Vollkorn-Wraps · Salat · Kichererbsen · Tahini-Dressing',
    itemsEn: '150 g seitan · 2 wholegrain wraps · salad · chickpeas · tahini dressing',
  },
  {
    key: 'prebanana',
    slot: 'pre',
    name: 'Banane + Reiswaffeln + Espresso',
    nameEn: 'Banana + rice cakes + espresso',
    kcal: 290,
    protein: 6,
    carbs: 58,
    fat: 4,
    items: '1 Banane · 3 Reiswaffeln · 1 TL Marmelade · Espresso (Koffein optional)',
    itemsEn: '1 banana · 3 rice cakes · 1 tsp jam · espresso (caffeine optional)',
  },
  {
    key: 'predates',
    slot: 'pre',
    name: 'Datteln + Sojajoghurt',
    nameEn: 'Dates + soy yoghurt',
    kcal: 320,
    protein: 14,
    carbs: 54,
    fat: 5,
    items: '4 Datteln · 200 g Sojajoghurt natur · Zimt',
    itemsEn: '4 dates · 200 g plain soy yoghurt · cinnamon',
  },
  {
    key: 'preoat',
    slot: 'pre',
    name: 'Kleine Haferportion',
    nameEn: 'Small oat portion',
    kcal: 350,
    protein: 15,
    carbs: 60,
    fat: 6,
    items: '50 g Haferflocken · 200 ml Sojamilch · Ahornsirup',
    itemsEn: '50 g oats · 200 ml soy milk · maple syrup',
  },
  {
    key: 'postshake',
    slot: 'post',
    name: 'Proteinshake + Banane',
    nameEn: 'Protein shake + banana',
    kcal: 380,
    protein: 35,
    carbs: 48,
    fat: 5,
    items: '30 g Sojaprotein · 300 ml Sojamilch · 1 Banane',
    itemsEn: '30 g soy protein · 300 ml soy milk · 1 banana',
  },
  {
    key: 'postyog',
    slot: 'post',
    name: 'Sojajoghurt-Bowl',
    nameEn: 'Soy yoghurt bowl',
    kcal: 450,
    protein: 34,
    carbs: 52,
    fat: 12,
    items: '300 g Sojajoghurt · 25 g Protein · 40 g Müsli · Beeren',
    itemsEn: '300 g soy yoghurt · 25 g protein · 40 g muesli · berries',
  },
  {
    key: 'postbeans',
    slot: 'post',
    name: 'Kichererbsen-Couscous',
    nameEn: 'Chickpea couscous',
    kcal: 700,
    protein: 38,
    carbs: 96,
    fat: 16,
    items: '90 g Couscous · 200 g Kichererbsen · Gemüse · Tahini',
    itemsEn: '90 g couscous · 200 g chickpeas · vegetables · tahini',
  },
  {
    key: 'tofucurry',
    slot: 'dinner',
    name: 'Tofu-Curry mit Reis',
    nameEn: 'Tofu curry with rice',
    kcal: 720,
    protein: 46,
    carbs: 76,
    fat: 26,
    items: '200 g Tofu · 80 g Reis · Kokosmilch light · Gemüse · Currypaste',
    itemsEn: '200 g tofu · 80 g rice · light coconut milk · vegetables · curry paste',
  },
  {
    key: 'chili',
    slot: 'dinner',
    name: 'Chili sin Carne',
    nameEn: 'Chili sin carne',
    kcal: 640,
    protein: 40,
    carbs: 82,
    fat: 14,
    items: 'Kidneybohnen · Sojaschnetzel · Mais · Tomaten · Vollkornbrot',
    itemsEn: 'Kidney beans · soy mince · corn · tomatoes · wholegrain bread',
  },
  {
    key: 'buddha',
    slot: 'dinner',
    name: 'Ofengemüse + Edamame + Quinoa',
    nameEn: 'Roast vegetables + edamame + quinoa',
    kcal: 660,
    protein: 38,
    carbs: 72,
    fat: 22,
    items: '80 g Quinoa · 200 g Edamame · Ofengemüse · Tahini-Zitrone',
    itemsEn: '80 g quinoa · 200 g edamame · roast vegetables · tahini-lemon',
  },
  {
    key: 'nuts',
    slot: 'snack',
    name: 'Nussmix + Apfel',
    nameEn: 'Nut mix + apple',
    kcal: 320,
    protein: 10,
    carbs: 28,
    fat: 20,
    items: '35 g Nüsse · 1 Apfel',
    itemsEn: '35 g nuts · 1 apple',
  },
  {
    key: 'edamame',
    slot: 'snack',
    name: 'Edamame gesalzen',
    nameEn: 'Salted edamame',
    kcal: 220,
    protein: 20,
    carbs: 16,
    fat: 9,
    items: '200 g Edamame',
    itemsEn: '200 g edamame',
  },
  {
    key: 'skyr',
    slot: 'snack',
    name: 'Soja-„Skyr" + Beeren',
    nameEn: 'Soy "skyr" + berries',
    kcal: 200,
    protein: 22,
    carbs: 18,
    fat: 4,
    items: '250 g Soja-Skyr · 100 g Beeren',
    itemsEn: '250 g soy skyr · 100 g berries',
  },
];

/** Steht an jedem Startrezept — die App zeigt es an, statt Genauigkeit vorzutäuschen. */
export const STARTER_RECIPE_SOURCE =
  'Aus dem Prototyp übernommen — eigene Schätzung je Portion, nicht nachgewogen.';
export const STARTER_RECIPE_SOURCE_EN =
  'Carried over from the prototype — own estimate per portion, not weighed.';

/**
 * Faustregeln aus dem Fachkonzept §2, wörtlich übernommen.
 *
 * Sie stehen in der Ernährungsansicht, weil die App protokolliert und nicht
 * verordnet: Hinweise mit Quelle, keine Vorgaben.
 */
export const NUTRITION_RULES: readonly { de: string; en: string }[] = [
  {
    de: 'Protein 1,8–2,0 g/kg — vegan eher am oberen Rand, weil pflanzliches Protein etwas schlechter verwertet wird.',
    en: 'Protein 1.8–2.0 g/kg — vegans at the upper end, since plant protein is used slightly less efficiently.',
  },
  {
    de: 'Auf 4–5 Portionen à 30–40 g Protein verteilen, statt alles abends.',
    en: 'Spread it over 4–5 portions of 30–40 g protein instead of all in the evening.',
  },
  {
    de: 'Post-Workout-Timing ist nicht kritisch. Die Tagesmenge entscheidet.',
    en: 'Post-workout timing is not critical. The daily total is what counts.',
  },
  {
    de: 'Hülsenfrüchte und Getreide kombinieren — passiert bei normaler Mischkost von allein.',
    en: 'Combine legumes and grains — normal mixed meals do this on their own.',
  },
  {
    de: 'Vitamin C zu eisenreichen Mahlzeiten verbessert die Eisenaufnahme deutlich.',
    en: 'Vitamin C with iron-rich meals markedly improves iron uptake.',
  },
  {
    de: 'Kaffee und Tee mit einer Stunde Abstand zu eisenreichen Mahlzeiten.',
    en: 'Keep coffee and tea an hour away from iron-rich meals.',
  },
];

// --- Schnell eintragen (E11) ---------------------------------------------------

export interface LoggedMeal {
  id: string;
  date: string;
  slot: string;
  label: string;
  kcal: number;
  protein: number;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Die zuletzt gegessenen **verschiedenen** Einträge — zum erneuten Eintragen
 * mit einem Tipp. Gleich ist, was gleich heißt und gleich viele kcal hat
 * („Haferflocken 80 g" und „Haferflocken 40 g" sind zwei). Was heute schon in
 * dieser Mahlzeit steht, fehlt — doppelt tippt man sonst aus Versehen.
 * Einträge derselben Mahlzeit stehen vorn: Mittags will man Mittagessen.
 */
export function recentMeals<T extends LoggedMeal>(
  entries: readonly T[],
  options: { slot: string; today: string; limit: number },
): T[] {
  const key = (entry: LoggedMeal) =>
    `${entry.label.trim().toLowerCase()}|${Math.round(entry.kcal)}`;
  const already = new Set(
    entries
      .filter(
        (entry) => !entry.deletedAt && entry.date === options.today && entry.slot === options.slot,
      )
      .map(key),
  );
  const newest = [...entries]
    .filter((entry) => !entry.deletedAt && entry.date <= options.today)
    .sort((a, b) => b.date.localeCompare(a.date) || b.updatedAt.localeCompare(a.updatedAt));
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const entry of newest) {
    const id = key(entry);
    if (seen.has(id) || already.has(id)) continue;
    seen.add(id);
    unique.push(entry);
  }
  const sameSlot = unique.filter((entry) => entry.slot === options.slot);
  const other = unique.filter((entry) => entry.slot !== options.slot);
  return [...sameSlot, ...other].slice(0, options.limit);
}
